<app-modal [isOpen]="isOpen" (close)="onClose()" className="max-w-3xl mx-4 sm:mx-6">
  @if (lead) {
  <div class="p-6 sm:p-8">
    <!-- Header with Avatar -->
    <div class="flex items-start gap-6 mb-8">
      <!-- Avatar Circle with Initials -->
      <div class="flex-shrink-0">
        <div class="flex h-20 w-20 items-center justify-center rounded-full {{ avatarColorClass }}">
          <span class="text-2xl font-semibold">{{ initials }}</span>
        </div>
      </div>

      <!-- Name and badges -->
      <div class="flex-1 min-w-0">
        <h2 class="text-2xl font-bold text-gray-900 dark:text-white mb-2">
          {{ lead.name }}
        </h2>
        <div class="flex flex-wrap gap-2">
          @if (lead.isDisqualified) {
            <app-badge color="error">Descalificado</app-badge>
          } @else {
            <app-badge color="success">Activo</app-badge>
          }
          @if (lead.isRetryScheduled && lead.retryDate) {
            <app-badge color="warning">Reintentar: {{ formatDateShort(lead.retryDate) }}</app-badge>
          }
        </div>
      </div>

      <!-- Responsable + quick actions -->
      <div class="mt-auto pt-4 flex flex-col items-end gap-2">
        <app-quick-actions-menu
          label="Acciones"
          align="right"
          [leadId]="lead.id"
          [leadName]="lead.name"
          [contactId]="lead.idContact"
          [contactName]="contact ? ((contact.fullname || '') + (contact.lastname ? ' ' + contact.lastname : '')).trim() : ''"
          [contactEmail]="contact?.mail"
          [contactPhone]="contact?.phone"
          [companyId]="lead.idCompany"
          [companyName]="company?.commercialName"
          (actionTriggered)="onClose()">
        </app-quick-actions-menu>
        <div class="flex items-center gap-2 justify-end">
          <span class="text-sm font-medium text-gray-700 dark:text-gray-300">Responsable:</span>
          @if (loadingUsers) {
            <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-brand-500"></div>
          } @else if (ownerUser) {
            <span class="text-sm font-medium text-gray-900 dark:text-white">
              {{ ownerUser.fullname }} {{ ownerUser.lastname }}
            </span>
          } @else {
            <span class="text-sm text-gray-400 dark:text-gray-500">Sin información</span>
          }
        </div>
      </div>
    </div>

    <!-- Tab navigation -->
    <div class="border-b border-gray-200 dark:border-gray-700 mb-6">
      <nav class="-mb-px flex gap-1">
        <button
          (click)="onTabChange('info')"
          class="whitespace-nowrap px-4 py-2.5 text-sm font-medium border-b-2 transition-colors"
          [class.border-brand-500]="activeTab === 'info'"
          [class.text-brand-600]="activeTab === 'info'"
          [class.dark:text-brand-400]="activeTab === 'info'"
          [class.border-transparent]="activeTab !== 'info'"
          [class.text-gray-500]="activeTab !== 'info'"
          [class.hover:text-gray-700]="activeTab !== 'info'"
          [class.dark:text-gray-400]="activeTab !== 'info'">
          Información
        </button>
        <button
          (click)="onTabChange('timeline')"
          class="whitespace-nowrap px-4 py-2.5 text-sm font-medium border-b-2 transition-colors"
          [class.border-brand-500]="activeTab === 'timeline'"
          [class.text-brand-600]="activeTab === 'timeline'"
          [class.dark:text-brand-400]="activeTab === 'timeline'"
          [class.border-transparent]="activeTab !== 'timeline'"
          [class.text-gray-500]="activeTab !== 'timeline'"
          [class.hover:text-gray-700]="activeTab !== 'timeline'"
          [class.dark:text-gray-400]="activeTab !== 'timeline'">
          Línea de Tiempo
        </button>
      </nav>
    </div>

    <!-- ========== TAB: INFO (Inc 30: secciones colapsables) ========== -->
    @if (activeTab === 'info') {
    <div class="space-y-3">

      <!-- Sección: Información general -->
      <div class="rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
        <button type="button" (click)="toggleSection('general')"
          class="w-full flex items-center justify-between px-4 py-3 bg-gray-50 dark:bg-gray-800/50 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">
          <span class="text-sm font-semibold text-gray-700 dark:text-gray-200">Información general</span>
          <svg class="w-4 h-4 text-gray-400 transition-transform" [class.rotate-180]="isSectionOpen('general')"
            fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
          </svg>
        </button>
        @if (isSectionOpen('general')) {
        <div class="px-4 py-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <!-- Ciclo -->
          <div class="flex items-start gap-3">
            <div class="flex-shrink-0 mt-0.5">
              <svg class="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
              </svg>
            </div>
            <div class="min-w-0 flex-1">
              <p class="text-xs text-gray-500 dark:text-gray-400">Ciclo</p>
              <p class="text-sm font-medium text-gray-900 dark:text-white">{{ getCycleName() }}</p>
            </div>
          </div>

          <!-- Tipo (lead_state) -->
          <div class="flex items-start gap-3">
            <div class="flex-shrink-0 mt-0.5">
              <svg class="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
            </div>
            <div class="min-w-0 flex-1">
              <p class="text-xs text-gray-500 dark:text-gray-400">Tipo</p>
              <p class="text-sm font-medium text-gray-900 dark:text-white">{{ getStateName() }}</p>
            </div>
          </div>

          <!-- Responsable (Inc 32) -->
          <div class="flex items-start gap-3">
            <div class="flex-shrink-0 mt-0.5">
              <svg class="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>
              </svg>
            </div>
            <div class="min-w-0 flex-1">
              <p class="text-xs text-gray-500 dark:text-gray-400">Responsable</p>
              @if (loadingUsers) {
                <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-brand-500 mt-1"></div>
              } @else if (ownerUser) {
                <p class="text-sm font-medium text-gray-900 dark:text-white">
                  {{ ownerUser.fullname }} {{ ownerUser.lastname }}
                </p>
              } @else {
                <p class="text-sm text-gray-400 dark:text-gray-500">Sin información</p>
              }
            </div>
          </div>

          <!-- Fecha de reintento -->
          @if (lead.isRetryScheduled && lead.retryDate) {
          <div class="flex items-start gap-3">
            <div class="flex-shrink-0 mt-0.5">
              <svg class="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
            </div>
            <div class="min-w-0 flex-1">
              <p class="text-xs text-gray-500 dark:text-gray-400">Fecha de Reintento</p>
              <p class="text-sm font-medium text-gray-900 dark:text-white">{{ formatDateShort(lead.retryDate) }}</p>
            </div>
          </div>
          }
        </div>
        }
      </div>

      <!-- Sección: Contacto y Empresa -->
      <div class="rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
        <button type="button" (click)="toggleSection('contactoEmpresa')"
          class="w-full flex items-center justify-between px-4 py-3 bg-gray-50 dark:bg-gray-800/50 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">
          <span class="text-sm font-semibold text-gray-700 dark:text-gray-200">Contacto y Empresa</span>
          <svg class="w-4 h-4 text-gray-400 transition-transform" [class.rotate-180]="isSectionOpen('contactoEmpresa')"
            fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
          </svg>
        </button>
        @if (isSectionOpen('contactoEmpresa')) {
        <div class="px-4 py-4 space-y-4">
          <!-- Contacto -->
          <div>
            <p class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">Contacto</p>
            @if (loadingContact) {
              <div class="flex items-center gap-3 py-2">
                <div class="animate-spin rounded-full h-5 w-5 border-b-2 border-brand-500"></div>
                <span class="text-sm text-gray-500 dark:text-gray-400">Cargando contacto...</span>
              </div>
            } @else if (!lead.idContact) {
              <p class="text-sm text-gray-400 dark:text-gray-500 py-1">Sin contacto asociado.</p>
            } @else if (contact) {
              <div class="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-100 dark:border-gray-700/50">
                <div class="flex-shrink-0 flex h-10 w-10 items-center justify-center rounded-full bg-brand-100 text-brand-600 dark:bg-brand-900/30 dark:text-brand-400 text-sm font-semibold">
                  {{ (contact.fullname ? contact.fullname.charAt(0) : '') + (contact.lastname ? contact.lastname.charAt(0) : '') | uppercase }}
                </div>
                <div class="min-w-0 flex-1">
                  <p class="text-sm font-medium text-gray-900 dark:text-white">{{ contactFullName }}</p>
                  <div class="flex flex-wrap gap-3 mt-0.5">
                    @if (contact.mail) { <span class="text-xs text-gray-500 dark:text-gray-400">{{ contact.mail }}</span> }
                    @if (contact.phone) { <span class="text-xs text-gray-500 dark:text-gray-400">{{ contact.phone }}</span> }
                  </div>
                </div>
              </div>
            } @else {
              <p class="text-sm text-gray-400 dark:text-gray-500 py-1">No se pudo cargar el contacto.</p>
            }
          </div>

          <!-- Empresa -->
          <div>
            <p class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">Empresa</p>
            @if (loadingCompany) {
              <div class="flex items-center gap-3 py-2">
                <div class="animate-spin rounded-full h-5 w-5 border-b-2 border-brand-500"></div>
                <span class="text-sm text-gray-500 dark:text-gray-400">Cargando empresa...</span>
              </div>
            } @else if (!lead.idCompany) {
              <p class="text-sm text-gray-400 dark:text-gray-500 py-1">Sin empresa asociada.</p>
            } @else if (company) {
              <div class="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-100 dark:border-gray-700/50">
                <div class="flex-shrink-0 flex h-10 w-10 items-center justify-center rounded-full bg-orange-100 text-orange-600 dark:bg-orange-900/30 dark:text-orange-400 text-sm font-semibold">
                  {{ company.commercialName ? company.commercialName.substring(0, 2) : '' | uppercase }}
                </div>
                <div class="min-w-0 flex-1">
                  <p class="text-sm font-medium text-gray-900 dark:text-white">{{ company.commercialName }}</p>
                  <div class="flex flex-wrap gap-3 mt-0.5">
                    @if (company.phone) { <span class="text-xs text-gray-500 dark:text-gray-400">{{ company.phone }}</span> }
                    @if (company.website) { <span class="text-xs text-gray-500 dark:text-gray-400">{{ company.website }}</span> }
                  </div>
                </div>
              </div>
            } @else {
              <p class="text-sm text-gray-400 dark:text-gray-500 py-1">No se pudo cargar la empresa.</p>
            }
          </div>
        </div>
        }
      </div>

      <!-- Sección: Descalificación / Reintento -->
      @if (lead.isDisqualified) {
      <div class="rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
        <button type="button" (click)="toggleSection('descalificacion')"
          class="w-full flex items-center justify-between px-4 py-3 bg-gray-50 dark:bg-gray-800/50 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">
          <span class="text-sm font-semibold text-gray-700 dark:text-gray-200">Descalificación / Reintento</span>
          <svg class="w-4 h-4 text-gray-400 transition-transform" [class.rotate-180]="isSectionOpen('descalificacion')"
            fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
          </svg>
        </button>
        @if (isSectionOpen('descalificacion')) {
        <div class="px-4 py-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div class="flex items-start gap-3">
            <div class="flex-shrink-0 mt-0.5">
              <svg class="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636"></path>
              </svg>
            </div>
            <div class="min-w-0 flex-1">
              <p class="text-xs text-gray-500 dark:text-gray-400">Razón</p>
              <p class="text-sm font-medium text-gray-900 dark:text-white">{{ translateDisqualificationReason(getDisqualificationReasonName()) }}</p>
            </div>
          </div>
          @if (lead.disqualificationNotes) {
          <div class="flex items-start gap-3">
            <div class="flex-shrink-0 mt-0.5">
              <svg class="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z"></path>
              </svg>
            </div>
            <div class="min-w-0 flex-1">
              <p class="text-xs text-gray-500 dark:text-gray-400">Notas</p>
              <p class="text-sm font-medium text-gray-900 dark:text-white">{{ lead.disqualificationNotes }}</p>
            </div>
          </div>
          }
          @if (lead.isRetryScheduled && lead.retryDate) {
          <div class="flex items-start gap-3">
            <div class="flex-shrink-0 mt-0.5">
              <svg class="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
            </div>
            <div class="min-w-0 flex-1">
              <p class="text-xs text-gray-500 dark:text-gray-400">Reintento programado</p>
              <p class="text-sm font-medium text-gray-900 dark:text-white">{{ formatDateShort(lead.retryDate) }}</p>
            </div>
          </div>
          }
        </div>
        }
      </div>
      }

      <!-- Sección: Actividad -->
      <div class="rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
        <button type="button" (click)="toggleSection('actividad')"
          class="w-full flex items-center justify-between px-4 py-3 bg-gray-50 dark:bg-gray-800/50 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">
          <span class="text-sm font-semibold text-gray-700 dark:text-gray-200">Actividad</span>
          <svg class="w-4 h-4 text-gray-400 transition-transform" [class.rotate-180]="isSectionOpen('actividad')"
            fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
          </svg>
        </button>
        @if (isSectionOpen('actividad')) {
        <div class="px-4 py-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <!-- Prioridad -->
          <div class="flex items-start gap-3">
            <div class="flex-shrink-0 mt-0.5">
              <svg class="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"/>
              </svg>
            </div>
            <div class="min-w-0 flex-1">
              <p class="text-xs text-gray-500 dark:text-gray-400">Prioridad</p>
              @if (lead.priority) {
                <app-badge [color]="priorityColor(lead.priority)" size="sm">{{ priorityLabel(lead.priority) }}</app-badge>
              } @else {
                <p class="text-sm text-gray-400 dark:text-gray-500">—</p>
              }
            </div>
          </div>

          <!-- Próxima tarea -->
          <div class="flex items-start gap-3">
            <div class="flex-shrink-0 mt-0.5">
              <svg class="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"></path>
              </svg>
            </div>
            <div class="min-w-0 flex-1">
              <p class="text-xs text-gray-500 dark:text-gray-400">Próxima tarea</p>
              @if (lead.nextTaskTitle) {
                <p class="text-sm font-medium text-gray-900 dark:text-white">
                  {{ lead.nextTaskTitle }}
                  @if (lead.nextTaskDueDate) {
                    <span class="text-xs text-gray-400 dark:text-gray-500">· {{ formatDateShort(lead.nextTaskDueDate) }}</span>
                  }
                </p>
              } @else {
                <p class="text-sm text-gray-400 dark:text-gray-500">Sin tareas pendientes</p>
              }
            </div>
          </div>

          <!-- Última actividad -->
          <div class="flex items-start gap-3">
            <div class="flex-shrink-0 mt-0.5">
              <svg class="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
            </div>
            <div class="min-w-0 flex-1">
              <p class="text-xs text-gray-500 dark:text-gray-400">Última actividad</p>
              <p class="text-sm font-medium text-gray-900 dark:text-white">
                {{ lead.lastActivityAt ? formatDateTime(lead.lastActivityAt) : '—' }}
              </p>
            </div>
          </div>
        </div>
        }
      </div>

      <!-- Sección: Registro -->
      <div class="rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
        <button type="button" (click)="toggleSection('registro')"
          class="w-full flex items-center justify-between px-4 py-3 bg-gray-50 dark:bg-gray-800/50 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">
          <span class="text-sm font-semibold text-gray-700 dark:text-gray-200">Registro</span>
          <svg class="w-4 h-4 text-gray-400 transition-transform" [class.rotate-180]="isSectionOpen('registro')"
            fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
          </svg>
        </button>
        @if (isSectionOpen('registro')) {
        <div class="px-4 py-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <!-- Fecha de Creación -->
          <div class="flex items-start gap-3">
            <div class="flex-shrink-0 mt-0.5">
              <svg class="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
              </svg>
            </div>
            <div class="min-w-0 flex-1">
              <p class="text-xs text-gray-500 dark:text-gray-400">Fecha de Creación</p>
              <p class="text-sm font-medium text-gray-900 dark:text-white">{{ formatDateTime(lead.datetimeCreated) }}</p>
            </div>
          </div>

          <!-- Creado por -->
          <div class="flex items-start gap-3">
            <div class="flex-shrink-0 mt-0.5">
              <svg class="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>
              </svg>
            </div>
            <div class="min-w-0 flex-1">
              <p class="text-xs text-gray-500 dark:text-gray-400">Creado por</p>
              @if (loadingUsers) {
                <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-brand-500 mt-1"></div>
              } @else {
                <p class="text-sm font-medium text-gray-900 dark:text-white">
                  {{ registeredByUser ? registeredByUser.fullname + ' ' + (registeredByUser.lastname || '') : 'Sin información' }}
                </p>
              }
            </div>
          </div>

          <!-- Última Actualización -->
          <div class="flex items-start gap-3">
            <div class="flex-shrink-0 mt-0.5">
              <svg class="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
              </svg>
            </div>
            <div class="min-w-0 flex-1">
              <p class="text-xs text-gray-500 dark:text-gray-400">Última Actualización</p>
              <p class="text-sm font-medium text-gray-900 dark:text-white">{{ formatDateTime(lead.datetimeUpdated) }}</p>
            </div>
          </div>
        </div>
        }
      </div>
    </div>
    } <!-- end info tab -->

    <!-- ========== TAB: TIMELINE ========== -->
    @if (activeTab === 'timeline') {
      <app-audit-timeline
        [logs]="timelineLogs"
        [loading]="loadingTimeline"
        [error]="timelineError"
        [currentPage]="timelinePage"
        [totalPages]="timelineTotalPages"
        [totalElements]="timelineTotalElements"
        [linkableEntities]="timelineLinkableEntities"
        emptyMessage="No hay eventos de auditoría para este lead."
        (pageChange)="loadTimeline($event)"
        (entityLinkClick)="openTimelineEntity($event)" />
    } <!-- end timeline tab -->

  </div>
  }
</app-modal>

<!-- Inc 25: detalle de la tarea creada desde este lead (se abre desde el timeline) -->
<app-task-detail-modal
  [isOpen]="showTaskDetailModal"
  [taskId]="detailTaskId"
  (closed)="closeTaskDetail()" />

<!-- Inc 25: detalle del ticket creado desde este lead (se abre desde el timeline) -->
<app-tickets-detail-modal
  [isOpen]="showTicketDetailModal"
  [ticket]="selectedTicket"
  [priorities]="ticketPriorities"
  [statuses]="ticketStatuses"
  [categories]="ticketCategories"
  [pipelines]="ticketPipelines"
  (close)="closeTicketDetail()" />
